"use client"

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react"

export const WORKFLOW_STEP_IDS = [
  "barcode",
  "customer",
  "cart",
  "quantity",
  "rate",
  "discount",
  "payment-method",
  "amount-received",
  "complete-sale",
] as const

export type WorkflowStepId = (typeof WORKFLOW_STEP_IDS)[number]

export const WORKFLOW_LABELS: Record<WorkflowStepId, string> = {
  barcode: "Barcode",
  customer: "Customer",
  cart: "Cart",
  quantity: "Quantity",
  rate: "Rate (Rs)",
  discount: "Discount",
  "payment-method": "Payment Method",
  "amount-received": "Amount Received",
  "complete-sale": "Complete Sale",
}

export const DEFAULT_WORKFLOW_ORDER = WORKFLOW_STEP_IDS.map((id) => WORKFLOW_LABELS[id])

export type ShortcutAction =
  | "Focus Barcode"
  | "Customer Search"
  | "Hold Sale"
  | "Discount"
  | "Payment"
  | "Complete Sale"
  | "Cancel Sale"
  | "Print"
  | "Open List"

export const DEFAULT_SHORTCUTS: Record<ShortcutAction, string> = {
  "Complete Sale": "F2",
  "Print": "F3",
  "Hold Sale": "F4",
  "Payment": "F5",
  "Customer Search": "F6",
  "Open List": "F7",
  "Discount": "F9",
  "Focus Barcode": "F10",
  "Cancel Sale": "Esc",
}

/** Display order by key number. */
export const SHORTCUT_DISPLAY_ORDER: ShortcutAction[] = [
  "Complete Sale",
  "Print",
  "Hold Sale",
  "Payment",
  "Customer Search",
  "Open List",
  "Discount",
  "Focus Barcode",
  "Cancel Sale",
]

export type AccessibilitySectionId = "pos-register" | "invoice" | "purchase"

export const ACCESSIBILITY_SECTIONS: Record<
  AccessibilitySectionId,
  { label: string; description: string; showAutoBehaviors: boolean }
> = {
  "pos-register": {
    label: "POS Register",
    description: "Checkout screen keyboard flow and shortcuts.",
    showAutoBehaviors: true,
  },
  invoice: {
    label: "Invoice",
    description: "Sales invoice entry keyboard flow and shortcuts.",
    showAutoBehaviors: false,
  },
  purchase: {
    label: "Purchase",
    description: "Purchase order and stock receiving keyboard flow.",
    showAutoBehaviors: false,
  },
}

export const SECTION_WORKFLOW_LABELS: Record<AccessibilitySectionId, Record<WorkflowStepId, string>> = {
  "pos-register": { ...WORKFLOW_LABELS },
  invoice: { ...WORKFLOW_LABELS },
  purchase: {
    barcode: "Part No",
    customer: "Supplier",
    cart: "Cart",
    quantity: "Qty",
    rate: "Cost Rs",
    discount: "Discount",
    "payment-method": "Payment Method",
    "amount-received": "Amount Paid",
    "complete-sale": "Save",
  },
}

export function defaultWorkflowOrder(sectionId: AccessibilitySectionId): string[] {
  if (sectionId === "purchase") {
    return [
      "Doc No",
      "Date",
      "Ref. No.",
      "Supplier",
      "Staff",
      "Item Search",
      "Qty",
      "Cost (Rs)",
      "Disc",
      "Retail",
    ]
  }
  if (sectionId === "invoice") {
    return [
      "Invoice No",
      "Date",
      "Ref. No.",
      "Client",
      "Salesperson",
      "Sales Source",
      "Item Search",
      "Qty",
      "Rate (Rs)",
      "Disc",
    ]
  }
  const labels = SECTION_WORKFLOW_LABELS[sectionId]
  return [
    labels.barcode,
    labels.customer,
    labels.quantity,
    labels.rate,
    labels.discount,
    labels["payment-method"],
    labels["amount-received"],
    labels["complete-sale"],
  ]
}

const LEGACY_DOCUMENT_FLOW_ALIASES: Record<string, string> = {
  Barcode: "Item Search",
  "Part No": "Item Search",
  Customer: "Client",
  Supplier: "Client",
  Quantity: "Qty",
  "Cost Rs": "Cost (Rs)",
  Discount: "Disc",
  "Rate (Rs)": "Rate (Rs)",
  "Amount Received": "Amount Received",
  "Amount Paid": "Amount Paid",
  Save: "Save",
}

function migrateDocumentFlowLabel(label: string, sectionId: AccessibilitySectionId): string {
  if (sectionId !== "invoice" && sectionId !== "purchase") return label
  return LEGACY_DOCUMENT_FLOW_ALIASES[label] ?? label
}

/** Replace legacy "Cart" step with editable Quantity + Rate fields. */
export function normalizeWorkflowOrder(order: string[], sectionId: AccessibilitySectionId): string[] {
  if (sectionId === "invoice" || sectionId === "purchase") {
    const defaults = defaultWorkflowOrder(sectionId)
    const next: string[] = []
    for (const step of order) {
      const migrated = migrateDocumentFlowLabel(step, sectionId)
      if (
        migrated === "Cart"
        || migrated === "Amount Received"
        || migrated === "Amount Paid"
        || migrated === "Save"
        || migrated === "Payment Method"
        || migrated === "Complete Sale"
      ) {
        continue
      }
      if (!next.includes(migrated)) next.push(migrated)
    }
    for (const step of defaults) {
      if (!next.includes(step)) next.push(step)
    }
    if (sectionId === "purchase") {
      const retailIdx = next.indexOf("Retail")
      if (retailIdx >= 0) next.splice(retailIdx, 1)
      if (!next.includes("Retail")) {
        const discIdx = next.indexOf("Disc")
        const costIdx = next.indexOf("Cost (Rs)")
        const insertAt = discIdx >= 0 ? discIdx + 1 : costIdx >= 0 ? costIdx + 1 : next.length
        next.splice(insertAt, 0, "Retail")
      }
    }
    return next.length > 0 ? next : defaults
  }

  const labels = SECTION_WORKFLOW_LABELS[sectionId]
  const next: string[] = []
  for (const step of order) {
    if (step === labels.cart) {
      if (!next.includes(labels.quantity)) next.push(labels.quantity)
      if (!next.includes(labels.rate)) next.push(labels.rate)
    } else if (!next.includes(step)) {
      next.push(step)
    }
  }
  return next.length > 0 ? next : defaultWorkflowOrder(sectionId)
}

export type FieldBehavior = "enable" | "skip" | "disable" | "hide"

export interface ScreenFieldDef {
  key: string
  label: string
  defaultBehavior: FieldBehavior
}

export const POS_SCREEN_FIELDS: ScreenFieldDef[] = [
  { key: "partNo", label: "Part No.", defaultBehavior: "enable" },
  { key: "details", label: "Details", defaultBehavior: "skip" },
  { key: "qty", label: "Qty", defaultBehavior: "skip" },
  { key: "foc", label: "FOC", defaultBehavior: "disable" },
  { key: "batch", label: "Batch", defaultBehavior: "disable" },
  { key: "amount", label: "Amount", defaultBehavior: "skip" },
  { key: "tax", label: "Tax", defaultBehavior: "skip" },
  { key: "discount", label: "Discount", defaultBehavior: "skip" },
  { key: "total", label: "Total", defaultBehavior: "skip" },
  { key: "user", label: "User", defaultBehavior: "hide" },
]

export const SALES_SCREEN_FIELDS: ScreenFieldDef[] = [
  { key: "details", label: "Details", defaultBehavior: "enable" },
  { key: "qty", label: "Qty", defaultBehavior: "enable" },
  { key: "foc", label: "FOC", defaultBehavior: "enable" },
  { key: "batch", label: "Batch", defaultBehavior: "enable" },
  { key: "expiry", label: "Expiry", defaultBehavior: "disable" },
  { key: "amount", label: "Amount", defaultBehavior: "enable" },
  { key: "discount", label: "Discount", defaultBehavior: "enable" },
  { key: "tax", label: "Tax", defaultBehavior: "enable" },
  { key: "total", label: "Total", defaultBehavior: "enable" },
  { key: "user", label: "User", defaultBehavior: "hide" },
]

export const PURCHASE_SCREEN_FIELDS: ScreenFieldDef[] = [
  { key: "details", label: "Details", defaultBehavior: "enable" },
  { key: "qty", label: "Qty", defaultBehavior: "enable" },
  { key: "expiry", label: "Expiry", defaultBehavior: "enable" },
  { key: "batch", label: "Batch", defaultBehavior: "enable" },
  { key: "cost", label: "Cost", defaultBehavior: "enable" },
  { key: "discount", label: "Discount", defaultBehavior: "enable" },
  { key: "tax", label: "Tax", defaultBehavior: "enable" },
  { key: "total", label: "Total", defaultBehavior: "enable" },
  { key: "retail", label: "Retail", defaultBehavior: "hide" },
]

export const SCREEN_FIELDS_BY_SECTION: Record<AccessibilitySectionId, ScreenFieldDef[]> = {
  "pos-register": POS_SCREEN_FIELDS,
  invoice: SALES_SCREEN_FIELDS,
  purchase: PURCHASE_SCREEN_FIELDS,
}

export function defaultScreenFields(sectionId: AccessibilitySectionId): Record<string, FieldBehavior> {
  const defs = SCREEN_FIELDS_BY_SECTION[sectionId] || []
  return Object.fromEntries(defs.map((d) => [d.key, d.defaultBehavior]))
}

export function getFieldBehavior(
  section: SectionAccessibilitySettings | undefined,
  fieldKey: string,
  sectionId?: AccessibilitySectionId,
): FieldBehavior {
  if (!section) return "enable"
  if (section.screenFields?.[fieldKey]) return section.screenFields[fieldKey]
  if (sectionId) {
    const def = SCREEN_FIELDS_BY_SECTION[sectionId]?.find((d) => d.key === fieldKey)
    if (def) return def.defaultBehavior
  }
  return "enable"
}

export function isFieldHidden(
  section: SectionAccessibilitySettings | undefined,
  fieldKey: string,
  sectionId?: AccessibilitySectionId,
): boolean {
  return getFieldBehavior(section, fieldKey, sectionId) === "hide"
}

export function isFieldDisabled(
  section: SectionAccessibilitySettings | undefined,
  fieldKey: string,
  sectionId?: AccessibilitySectionId,
): boolean {
  return getFieldBehavior(section, fieldKey, sectionId) === "disable"
}

export function isFieldSkipped(
  section: SectionAccessibilitySettings | undefined,
  fieldKey: string,
  sectionId?: AccessibilitySectionId,
): boolean {
  return getFieldBehavior(section, fieldKey, sectionId) === "skip"
}

export interface KeyboardNavSettings {
  keyboardFirst: boolean
  enterNext: boolean
  tabNext: boolean
  autoFocusBarcode: boolean
  autoFocusAfterSale: boolean
  skipDisabled: boolean
  skipOptional: boolean
}

export interface AutoBehaviorSettings {
  autoAddScanned: boolean
  autoIncreaseQty: boolean
  autoOpenPayment: boolean
  autoPrintReceipt: boolean
  autoOpenDrawer: boolean
  autoNewSale: boolean
  requireCustomer: boolean
  requirePinDiscount: boolean
}

export interface UiPreferenceSettings {
  compactMode: boolean
  largeButtons: boolean
  darkMode: boolean
  showImages: boolean
  showStock: boolean
  showProfitMargin: boolean
  showTaxes: boolean
}

export interface SectionAccessibilitySettings {
  keyboardNav: KeyboardNavSettings
  workflowOrder: string[]
  shortcuts: Record<ShortcutAction, string>
  autoBehaviors: AutoBehaviorSettings
  screenFields: Record<string, FieldBehavior>
}

export interface AccessibilitySettings {
  sections: Record<AccessibilitySectionId, SectionAccessibilitySettings>
  uiPrefs: UiPreferenceSettings
}

const DEFAULT_KEYBOARD_NAV: KeyboardNavSettings = {
  keyboardFirst: false,
  enterNext: true,
  tabNext: false,
  autoFocusBarcode: true,
  autoFocusAfterSale: true,
  skipDisabled: true,
  skipOptional: false,
}

const DEFAULT_AUTO_BEHAVIORS: AutoBehaviorSettings = {
  autoAddScanned: true,
  autoIncreaseQty: true,
  autoOpenPayment: false,
  autoPrintReceipt: false,
  autoOpenDrawer: false,
  autoNewSale: false,
  requireCustomer: false,
  requirePinDiscount: false,
}

function createDefaultSection(sectionId: AccessibilitySectionId): SectionAccessibilitySettings {
  const keyboardNav =
    sectionId === "invoice" || sectionId === "purchase"
      ? { ...DEFAULT_KEYBOARD_NAV, tabNext: true }
      : { ...DEFAULT_KEYBOARD_NAV }
  return {
    keyboardNav,
    workflowOrder: defaultWorkflowOrder(sectionId),
    shortcuts: { ...DEFAULT_SHORTCUTS },
    autoBehaviors: { ...DEFAULT_AUTO_BEHAVIORS },
    screenFields: defaultScreenFields(sectionId),
  }
}

export const DEFAULT_ACCESSIBILITY_SETTINGS: AccessibilitySettings = {
  sections: {
    "pos-register": createDefaultSection("pos-register"),
    invoice: createDefaultSection("invoice"),
    purchase: createDefaultSection("purchase"),
  },
  uiPrefs: {
    compactMode: false,
    largeButtons: false,
    darkMode: false,
    showImages: true,
    showStock: true,
    showProfitMargin: false,
    showTaxes: false,
  },
}

const STORAGE_KEY = "pos-accessibility-settings"

function normalizeShortcuts(shortcuts: Record<ShortcutAction, string>): Record<ShortcutAction, string> {
  const next = { ...shortcuts }
  // Migrate older defaults to the current layout: F2 Save, F6 Customer, F9 Discount, F10 Barcode.
  if (next["Complete Sale"] === "Ctrl+Enter") next["Complete Sale"] = "F2"
  if (next["Focus Barcode"] === "F2" || next["Focus Barcode"] === "F8") next["Focus Barcode"] = "F10"
  if (next["Customer Search"] === "F3") next["Customer Search"] = "F6"
  if (!next["Print"]) next["Print"] = "F3"
  if (!next["Open List"]) next["Open List"] = "F7"
  if (next["Discount"] === "F5") next["Discount"] = "F9"
  if (next["Payment"] === "F6" || next["Payment"] === "F7") next["Payment"] = "F5"
  return next
}

function migrateLegacySettings(parsed: Record<string, unknown>): AccessibilitySettings {
  const legacy = parsed as {
    keyboardNav?: KeyboardNavSettings
    workflowOrder?: string[]
    shortcuts?: Record<ShortcutAction, string>
    autoBehaviors?: AutoBehaviorSettings
    uiPrefs?: UiPreferenceSettings
    screenFields?: Record<string, FieldBehavior>
  }
  const base = createDefaultSection("pos-register")
  const shared = {
    keyboardNav: { ...base.keyboardNav, ...legacy.keyboardNav },
    workflowOrder: legacy.workflowOrder?.length
      ? normalizeWorkflowOrder(legacy.workflowOrder, "pos-register")
      : base.workflowOrder,
    shortcuts: normalizeShortcuts({ ...base.shortcuts, ...legacy.shortcuts }),
    autoBehaviors: { ...base.autoBehaviors, ...legacy.autoBehaviors },
    screenFields: { ...base.screenFields, ...legacy.screenFields },
  }
  return {
    sections: {
      "pos-register": { ...createDefaultSection("pos-register"), ...shared },
      invoice: {
        ...createDefaultSection("invoice"),
        keyboardNav: { ...shared.keyboardNav },
        shortcuts: { ...shared.shortcuts },
      },
      purchase: {
        ...createDefaultSection("purchase"),
        keyboardNav: { ...shared.keyboardNav },
        shortcuts: { ...shared.shortcuts },
        autoBehaviors: { ...shared.autoBehaviors },
      },
    },
    uiPrefs: { ...DEFAULT_ACCESSIBILITY_SETTINGS.uiPrefs, ...legacy.uiPrefs },
  }
}

function loadSettings(): AccessibilitySettings {
  if (typeof window === "undefined") return DEFAULT_ACCESSIBILITY_SETTINGS
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULT_ACCESSIBILITY_SETTINGS
    const parsed = JSON.parse(raw) as Record<string, unknown>
    if (parsed.sections) {
      const sections = parsed.sections as Partial<Record<AccessibilitySectionId, Partial<SectionAccessibilitySettings>>>
      const mergeSection = (id: AccessibilitySectionId): SectionAccessibilitySettings => {
        const defaults = createDefaultSection(id)
        const saved = sections[id]
        if (!saved) return defaults
        return {
          keyboardNav: { ...defaults.keyboardNav, ...saved.keyboardNav },
          workflowOrder: saved.workflowOrder?.length
            ? normalizeWorkflowOrder(saved.workflowOrder, id)
            : defaults.workflowOrder,
          shortcuts: normalizeShortcuts({ ...defaults.shortcuts, ...saved.shortcuts }),
          autoBehaviors: { ...defaults.autoBehaviors, ...saved.autoBehaviors },
          screenFields: { ...defaults.screenFields, ...saved.screenFields },
        }
      }
      return {
        sections: {
          "pos-register": mergeSection("pos-register"),
          invoice: mergeSection("invoice"),
          purchase: mergeSection("purchase"),
        },
        uiPrefs: { ...DEFAULT_ACCESSIBILITY_SETTINGS.uiPrefs, ...(parsed.uiPrefs as UiPreferenceSettings | undefined) },
      }
    }
    return migrateLegacySettings(parsed)
  } catch {
    return DEFAULT_ACCESSIBILITY_SETTINGS
  }
}

export function getAccessibilitySettings(): AccessibilitySettings {
  return loadSettings()
}

export function getSectionSettings(sectionId: AccessibilitySectionId): SectionAccessibilitySettings {
  return getAccessibilitySettings().sections[sectionId]
}

function saveSettings(settings: AccessibilitySettings) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
}

interface AccessibilitySettingsContextType {
  settings: AccessibilitySettings
  updateSection: (
    sectionId: AccessibilitySectionId,
    updater: (prev: SectionAccessibilitySettings) => SectionAccessibilitySettings,
  ) => void
  setUiPrefs: (value: UiPreferenceSettings | ((prev: UiPreferenceSettings) => UiPreferenceSettings)) => void
}

const AccessibilitySettingsContext = createContext<AccessibilitySettingsContextType | undefined>(undefined)

export function AccessibilitySettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<AccessibilitySettings>(DEFAULT_ACCESSIBILITY_SETTINGS)

  useEffect(() => {
    setSettings(loadSettings())
  }, [])

  useEffect(() => {
    const anyKeyboardFirst = Object.values(settings.sections).some((s) => s.keyboardNav.keyboardFirst)
    document.documentElement.dataset.keyboardFirst = anyKeyboardFirst ? "true" : ""
  }, [settings.sections])

  const updateSection = useCallback((
    sectionId: AccessibilitySectionId,
    updater: (prev: SectionAccessibilitySettings) => SectionAccessibilitySettings,
  ) => {
    setSettings((prev) => {
      const nextSection = updater(prev.sections[sectionId])
      const next = {
        ...prev,
        sections: { ...prev.sections, [sectionId]: nextSection },
      }
      saveSettings(next)
      return next
    })
  }, [])

  const setUiPrefs = useCallback((value: UiPreferenceSettings | ((prev: UiPreferenceSettings) => UiPreferenceSettings)) => {
    setSettings((prev) => {
      const uiPrefs = typeof value === "function" ? value(prev.uiPrefs) : value
      const next = { ...prev, uiPrefs }
      saveSettings(next)
      return next
    })
  }, [])

  return (
    <AccessibilitySettingsContext.Provider value={{ settings, updateSection, setUiPrefs }}>
      {children}
    </AccessibilitySettingsContext.Provider>
  )
}

export function useAccessibilitySettings() {
  const ctx = useContext(AccessibilitySettingsContext)
  if (!ctx) throw new Error("useAccessibilitySettings must be used within AccessibilitySettingsProvider")
  return ctx
}
